import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { useInvoiceSettingsShow, useInvoiceSummary } from '@jobilla/nexus-sdk'
import { getRouteApi, Link } from '@tanstack/react-router'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const companyRoute = getRouteApi('/_auth/company/$companyId')

export function OverdueInvoicesAlert() {
	const { t, i18n } = useTranslation('common')
	const { can } = useAuth()
	const organisationId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()

	const canManageBilling = can('billing:manage')

	const { data: invoiceSettings } = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId && canManageBilling,
		staleTime: 5 * 60 * 1000,
	})
	const externalInvoicing = invoiceSettings?.externalInvoicing ?? false

	const { data: summary } = useInvoiceSummary(organisationId, {
		enabled: !!organisationId && canManageBilling && !externalInvoicing,
		staleTime: 5 * 60 * 1000,
	})

	const overdueCount = summary?.overdue.count ?? 0
	if (overdueCount === 0) { return null }

	const formattedAmount = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: (summary?.overdue.currency ?? 'eur').toUpperCase(),
	}).format((summary?.overdue.amount ?? 0) / 100)

	return (
		<div
			role="alert"
			className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
		>
			<AlertTriangleIcon className="size-5 shrink-0 text-amber-600" aria-hidden />
			<div className="flex-1 text-sm">
				<div className="font-medium text-amber-900">
					{t('dashboard.overdueInvoices.title', { count: overdueCount })}
				</div>
				<div className="text-amber-800">
					{t('dashboard.overdueInvoices.body', { amount: formattedAmount })}
				</div>
			</div>
			<Link
				to="/company/$companyId/settings/invoices"
				params={{ companyId }}
				search={{ filter: 'overdue' }}
				className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
			>
				{t('dashboard.overdueInvoices.cta')}
			</Link>
		</div>
	)
}
